import { varAlpha } from 'minimal-shared/utils';

import {
  Box,
  Badge,
  Avatar,
  Tooltip,
  useTheme,
  MenuItem,
  Typography,
  IconButton,
  ListItemText,
} from '@mui/material';

import { usePremissionContext } from 'src/pages/auth/hooks';
import { truncateText } from 'src/pages/chat/utils/text-formatting';
import { UserPresenceAvatarWrap } from 'src/pages/chat/components/common/avatar';
import { hasBillingViewPermission } from 'src/pages/auth/hooks/permission-utils';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { BuyNowButton } from './buynow-button';
import { SignOutButton } from './sign-out-button';
import { BalanceDisplay } from './balance-display';
import { LanguagePopover } from './language-popover';
import { ToggleThemeSwitch } from './dark-light-button';
import { DialerPopover } from '../dashboard/call-dialer';
import { InternalCall } from '../dashboard/internal-call';
import { ChatAssignedTo } from './invite-user/chat-assigned-to';

// ----------------------------------------------------------------------

type Props = {
  loginApiData: any;
  profileImgUrl: string;
  t: (key: string) => string;
  registered: boolean;
  isConnecting: boolean;
  reconnectSIP: () => void;
  data: {
    id: string;
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
  selectedContactId: string[] | null;
  handleSelectContact: (id: string) => void;
  signOutHandler: () => void;
  onClose: () => void;
  activeSubscription: any;
  isView: boolean;
  isVoiceFeatureVisible: boolean;
  /** Corner presence dot for the signed-in user (global store + socket). */
  accountPresenceShow?: boolean;
  accountPresenceOnline?: boolean;
  accountUserId?: string;
};

export function AccountDrawerContent({
  loginApiData,
  profileImgUrl,
  t,
  registered,
  isConnecting,
  reconnectSIP,
  data,
  selectedContactId,
  handleSelectContact,
  signOutHandler,
  onClose,
  activeSubscription,
  isView,
  isVoiceFeatureVisible,
  accountPresenceShow,
  accountPresenceOnline,
  accountUserId,
}: Props) {
  const theme = useTheme();
  const { permissions } = usePremissionContext();
  const canShowBilling = hasBillingViewPermission(permissions);

  const renderLine = () => (
    <hr
      style={{
        width: '100%',
        border: `dashed 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.32)}`,
      }}
    />
  );

  return (
    <Scrollbar sx={{ height: 'auto', width: 280 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 1,
        }}
      >
        <Box
          sx={{
            p: '3px',
            borderRadius: '50%',
            display: 'inline-flex',
          }}
        >
          <UserPresenceAvatarWrap
            showPresence={!!(accountPresenceShow && accountUserId)}
            isOnline={!!accountPresenceOnline}
          >
            <Avatar
              alt={
                loginApiData && 'firstName' in loginApiData && 'lastName' in loginApiData
                  ? loginApiData.firstName || loginApiData?.lastName
                  : ''
              }
              src={
                profileImgUrl ||
                (loginApiData && 'firstName' in loginApiData && loginApiData.firstName) ||
                (loginApiData && 'lastName' in loginApiData && loginApiData.lastName) ||
                ''
              }
              sx={{ width: 48, height: 48 }}
            />
          </UserPresenceAvatarWrap>
        </Box>
        <Box>
          <Tooltip
            title={
              loginApiData &&
              'firstName' in loginApiData &&
              'lastName' in loginApiData &&
              `${loginApiData.firstName} ${loginApiData.lastName}`.length > 28
                ? `${loginApiData.firstName} ${loginApiData.lastName}`
                : ''
            }
            arrow
            placement="bottom"
          >
            <Typography variant="subtitle1" noWrap>
              {loginApiData && 'firstName' in loginApiData && 'lastName' in loginApiData
                ? truncateText(`${loginApiData.firstName} ${loginApiData.lastName}`, 20)
                : ''}
            </Typography>
          </Tooltip>

          {loginApiData && loginApiData?.email && (
            <Tooltip
              title={loginApiData?.email.length > 28 ? loginApiData?.email : ''}
              arrow
              placement="bottom"
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.1, fontSize: 12, cursor: 'pointer' }}
              >
                {truncateText(loginApiData?.email, 28)}
              </Typography>
            </Tooltip>
          )}

          {loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' &&
            isVoiceFeatureVisible && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.1, fontSize: 12 }}>
                  {t('WEBPHONE')}:{' '}
                  {loginApiData && 'extNumber' in loginApiData ? loginApiData.extNumber : ''}
                </Typography>
                <Badge
                  sx={{
                    position: 'relative',
                    '& .MuiBadge-badge': {
                      width: 8,
                      height: 8,
                    },
                  }}
                  badgeContent=""
                  variant={registered ? 'online' : 'offline'}
                />

                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.1, fontSize: 12 }}>
                  {!isConnecting ? (registered ? t('CONNECTED') : t('DISCONNECTED')) : ''}
                  {isConnecting && t('CONNECTING')}
                  <IconButton
                    size="small"
                    sx={{ width: 15, height: 15, ml: 0.5 }}
                    onClick={reconnectSIP}
                    disabled={isConnecting}
                  >
                    <Iconify icon="tdesign-refresh" sx={{ width: 12 }} />
                  </IconButton>
                </Typography>
              </Box>
            )}
        </Box>
      </Box>

      {/** @slot Balance display */}
      {loginApiData &&
        'tenant' in loginApiData &&
        typeof loginApiData.tenant === 'object' &&
        loginApiData.tenant !== null &&
        'name' in loginApiData.tenant &&
        (loginApiData.tenant as { name?: string }).name &&
        (loginApiData.tenant as { name?: string }).name !== 'MASTER' && (
          <Box sx={{ p: 2, pt: 0, display: { lg: 'none' } }}>
            <BalanceDisplay variant="compact" noActivePlan={activeSubscription === null} />
          </Box>
        )}

      <Box sx={{ display: { lg: 'none' } }}>{renderLine()}</Box>

      {/** @slot Tools & Settings */}
      <Box
        sx={{
          p: 2,
          display: { xs: 'flex', lg: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' &&
            isVoiceFeatureVisible && (
              <>
                <DialerPopover />
                <InternalCall />
              </>
            )}
          {isView &&
            loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' && (
              <ChatAssignedTo data={[]} />
            )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LanguagePopover
            data={[
              { value: 'en', label: 'English', countryCode: 'GB' },
              { value: 'ar', label: 'Arabic', countryCode: 'SA' },
            ]}
          />
          <ToggleThemeSwitch />
        </Box>
      </Box>

      <Box sx={{ display: { lg: 'none' } }}>{renderLine()}</Box>
      {data.map((contact) => {
        const isUserProfile = contact?.id === 'user-profile';

        const shouldShowUserProfile =
          isUserProfile &&
          loginApiData &&
          'tenant' in loginApiData &&
          typeof loginApiData.tenant === 'object' &&
          loginApiData.tenant !== null &&
          'name' in loginApiData.tenant &&
          (loginApiData.tenant as { name?: string }).name &&
          (loginApiData.tenant as { name?: string }).name !== 'MASTER';

        // Skip rendering 'user-profile' if tenant is MASTER
        if (isUserProfile && !shouldShowUserProfile) {
          return null;
        }

        return (
          <MenuItem
            key={contact.label}
            sx={{
              p: 1,
              px: 2,
              bgcolor: selectedContactId?.includes(contact.label) ? 'action.selected' : 'inherit',
            }}
            onClick={() => handleSelectContact(contact.href)}
          >
            <ListItemText
              primary={contact.label}
              slotProps={{
                secondary: {
                  sx: { typography: 'caption', color: 'text.disabled' },
                },
              }}
            />
          </MenuItem>
        );
      })}

      {renderLine()}
      {loginApiData &&
        'tenant' in loginApiData &&
        typeof loginApiData.tenant === 'object' &&
        loginApiData.tenant !== null &&
        'name' in loginApiData.tenant &&
        (loginApiData.tenant as { name?: string }).name &&
        (loginApiData.tenant as { name?: string }).name !== 'MASTER' &&
        loginApiData?.role !== 'User' &&
        canShowBilling && (
          <>
            <BuyNowButton handleMenuClose={() => onClose()} />
            {renderLine()}
          </>
        )}
      <SignOutButton onClose={signOutHandler} />
    </Scrollbar>
  );
}
